import classnames from "classnames"
import { useBgColorContext } from "./context"

import LearnMoreButton from "shared/components/learn_more_button"
import ReturnButton from "./return_button"
import ChatHeaderIconButton from "./chat_header_icon_button"
import UpgradeBanner from "./upgrade_banner"
import { steps } from "./steps"
import colors from "foundation/_colors_export.scss?export"
import { compareTier } from "./tiers"
import { Tier } from "./utils/interfaces"

import iconChevronLeft from "images/icons/icon-chevron-left.svg?raw"
import iconHelp from "images/icons/icon-help.svg?raw"

import styles from "./header.module.scss"

interface HeaderProps {
  aiPreviewEnablementRules: {
    block_user: boolean
    block_reason: string
    minimum_tier: string
  }
  editing: boolean
  backButtonTarget: () => void
  title: string
  updateLoading: (loading: boolean) => void
  user: {
    tier: Tier
    eligibleForTrial: boolean
    eligibleTrialPeriod?: number
    permissions: {
      [key: string]: {
        permitted: boolean
      }
    }
  }
  userAtQuota: boolean
  step: number
  showingCheckoutModal: boolean
  showUpgradeModal: (step: string, tier: Tier | undefined) => void
  appletTier: Tier
  chatStyle?: boolean
}

export const Header = ({
  aiPreviewEnablementRules,
  editing,
  backButtonTarget,
  title,
  updateLoading,
  user,
  userAtQuota,
  step,
  showingCheckoutModal,
  showUpgradeModal,
  appletTier,
  chatStyle = false,
}: HeaderProps) => {
  const onReturn = () => {
    if (initialStep) {
      updateLoading(true)
      window.App.Scripts.BackButton('/explore')
    } else {
      backButtonTarget()
    }
  }

  const bgColor = useBgColorContext()
  const bgOnColor = bgColor !== "white"
  const color = bgOnColor ? "white" : colors.primaryTextColor
  const classes = classnames(styles["diy-header"], {
    [styles["on-color"]]: bgOnColor,
    [styles["chat-style"]]: chatStyle,
  })

  const initialStep = step === steps.INITIAL
  const allowedScreenForBanner = (initialStep || step === steps.PREVIEW_OPEN_AI_RESULT) && !showingCheckoutModal

  let bannerClickHandler = () => {}
  let ctaReason = ""

  if (allowedScreenForBanner) {
    if (compareTier(appletTier, user.tier) > 0) {
      const aiPreviewBlockStep = aiPreviewEnablementRules?.block_user && aiPreviewEnablementRules.block_reason

      ctaReason = aiPreviewBlockStep || (appletTier === "pro" ? "default_pro" : "default_intermediate_pro")
      bannerClickHandler = () => showUpgradeModal(ctaReason, aiPreviewEnablementRules?.minimum_tier || appletTier)
    } else if (userAtQuota && !editing) {
      ctaReason = "applet_quota"
      bannerClickHandler = () => showUpgradeModal(ctaReason, user.tier === "free" ? "intermediate_pro" : "pro")
    }
  }

  return (
    <>
      <header style={{ backgroundColor: bgColor }} className={classes}>
        {chatStyle ? (
          <ChatHeaderIconButton icon={iconChevronLeft} title="Cancel" onClick={onReturn} />
        ) : (
          <ReturnButton onClick={onReturn} initialStep={initialStep} color={color} />
        )}
        {title && <h1 style={{ color }}>{title}</h1>}
        {!user.permissions?.zendesk_support?.permitted ? (
          chatStyle ? (
            // Restyle the LearnMore icon to match the chat header. zendesk-
            // permitted users keep the floating widget and its existing
            // open/close JS choreography untouched (an empty placeholder
            // here so flex-justify-between still positions the title).
            <ChatHeaderIconButton
              icon={iconHelp}
              title="Learn more: Best Practices - Applets 101"
              href="https://ift.tt/applets-101"
            />
          ) : (
            <LearnMoreButton
              url="https://ift.tt/applets-101"
              darkHeader={bgOnColor}
              title="Learn more: Best Practices - Applets 101"
            />
          )
        ) : (
          <div />
        )}
      </header>
      {allowedScreenForBanner && ctaReason && (
        <UpgradeBanner ctaReason={ctaReason} eligibleForTrial={user.eligibleForTrial} eligibleTrialPeriod={user.eligibleTrialPeriod} onClick={bannerClickHandler} appletTier={appletTier} />
      )}
    </>
  )
}

export default Header
